// Inspired by https://codepen.io/towc/pen/mJzOWJ
import { useCallback, useEffect, useRef } from "react";

interface DataFlowAnimationProps {
  className?: string;
}

interface Line {
  x: number;
  y: number;
  addedX: number;
  addedY: number;
  rad: number;
  lightInputMultiplier: number;
  color: string;
  cumulativeTime: number;
  time: number;
  targetTime: number;
  spawnX: number;
  spawnY: number;
}

const HEX_SIZE = 20;
const HEX_WIDTH = Math.sqrt(3) * HEX_SIZE;
const HEX_VERT_DIST = HEX_SIZE * 1.5;

const OPTS = {
  hexSize: HEX_SIZE,
  hexWidth: HEX_WIDTH,
  hexVertDist: HEX_VERT_DIST,
  len: HEX_WIDTH,
  count: 20,
  baseTime: 10,
  addedTime: 10,
  dieChance: 0.02,
  spawnChance: 0.3,
  sparkChance: 0.1,
  sparkDist: 10,
  sparkSize: 2,
  baseHue: 270,
  hueRange: 40,
  baseLight: 50,
  addedLight: 10,
  shadowToTimePropMult: 6,
  baseLightInputMultiplier: 0.01,
  addedLightInputMultiplier: 0.02,
  repaintAlpha: 0.035,
  hueChange: 0.1,
  mouseSpawnChance: 0.3,
  mouseHueInfluence: 20,
  mouseInfluenceRadius: 200,
  gridOpacity: 0.3,
};

const BASE_RAD = (Math.PI * 2) / 6;

export default function DataFlowAnimation({
  className,
}: DataFlowAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);
  const linesRef = useRef<Line[]>([]);
  const tickRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const dimsRef = useRef({ w: 0, h: 0, cx: 0, cy: 0, dieX: 0, dieY: 0 });
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const snapToHexGrid = useCallback(
    (x: number, y: number, cx: number, cy: number) => {
      const relY = y - cy;
      const row = Math.round(relY / OPTS.hexVertDist);
      const snappedY = cy + row * OPTS.hexVertDist;

      const relX = x - cx;
      const rowOffset = Math.abs(row) % 2 === 1 ? OPTS.hexWidth / 2 : 0;
      const col = Math.round((relX - rowOffset) / OPTS.hexWidth);
      const snappedX = cx + col * OPTS.hexWidth + rowOffset;

      return { x: snappedX, y: snappedY };
    },
    [],
  );

  const resetLineRef = useRef<(line: Line, spawnAtMouse?: boolean) => void>(
    null!,
  );

  const beginPhase = useCallback((line: Line) => {
    const dims = dimsRef.current;

    line.x += line.addedX;
    line.y += line.addedY;
    line.time = 0;
    line.targetTime = (OPTS.baseTime + OPTS.addedTime * Math.random()) | 0;
    line.rad += BASE_RAD * (Math.random() < 0.5 ? 1 : -1);
    line.addedX = Math.cos(line.rad);
    line.addedY = Math.sin(line.rad);

    if (
      Math.random() < OPTS.dieChance ||
      line.x > dims.dieX ||
      line.x < -dims.dieX ||
      line.y > dims.dieY ||
      line.y < -dims.dieY
    ) {
      resetLineRef.current?.(line, true);
    }
  }, []);

  const resetLine = useCallback(
    (line: Line, spawnAtMouse = false) => {
      const dims = dimsRef.current;
      const mouse = mouseRef.current;
      const useMouseSpawn =
        spawnAtMouse && mouse.active && Math.random() < OPTS.mouseSpawnChance;

      line.x = 0;
      line.y = 0;
      line.addedX = 0;
      line.addedY = 0;
      line.rad = 0;
      line.lightInputMultiplier =
        OPTS.baseLightInputMultiplier +
        OPTS.addedLightInputMultiplier * Math.random();
      line.color = `hsl(${OPTS.baseHue + (Math.random() - 0.5) * OPTS.hueRange}, 100%, light%)`;
      line.cumulativeTime = 0;
      line.time = 0;
      line.targetTime = (OPTS.baseTime + OPTS.addedTime * Math.random()) | 0;

      if (useMouseSpawn) {
        const snapped = snapToHexGrid(mouse.x, mouse.y, dims.cx, dims.cy);
        line.spawnX = snapped.x;
        line.spawnY = snapped.y;
      } else {
        line.spawnX = dims.cx;
        line.spawnY = dims.cy;
      }

      beginPhase(line);
    },
    [snapToHexGrid, beginPhase],
  );

  resetLineRef.current = resetLine;

  const createLine = useCallback(
    (spawnAtMouse = false): Line => {
      const dims = dimsRef.current;
      const mouse = mouseRef.current;
      const useMouseSpawn =
        spawnAtMouse && mouse.active && Math.random() < OPTS.mouseSpawnChance;

      let spawnX = dims.cx;
      let spawnY = dims.cy;

      if (useMouseSpawn) {
        const snapped = snapToHexGrid(mouse.x, mouse.y, dims.cx, dims.cy);
        spawnX = snapped.x;
        spawnY = snapped.y;
      }

      return {
        x: 0,
        y: 0,
        addedX: 0,
        addedY: 0,
        rad: 0,
        lightInputMultiplier:
          OPTS.baseLightInputMultiplier +
          OPTS.addedLightInputMultiplier * Math.random(),
        color: `hsl(${OPTS.baseHue + (Math.random() - 0.5) * OPTS.hueRange}, 100%, light%)`,
        cumulativeTime: 0,
        time: 0,
        targetTime: (OPTS.baseTime + OPTS.addedTime * Math.random()) | 0,
        spawnX,
        spawnY,
      };
    },
    [snapToHexGrid],
  );

  const getMouseHueOffset = useCallback(
    (screenX: number, screenY: number): number => {
      const mouse = mouseRef.current;
      if (!mouse.active) return 0;

      const dx = screenX - mouse.x;
      const dy = screenY - mouse.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > OPTS.mouseInfluenceRadius) return 0;

      const influence = 1 - dist / OPTS.mouseInfluenceRadius;
      return OPTS.mouseHueInfluence * influence * (dx > 0 ? 1 : -1);
    },
    [],
  );

  const stepLine = useCallback(
    (ctx: CanvasRenderingContext2D, line: Line) => {
      line.time++;
      line.cumulativeTime++;

      if (line.time >= line.targetTime) {
        beginPhase(line);
      }

      const prop = line.time / line.targetTime;
      const wave = Math.sin((prop * Math.PI) / 2);
      const x = line.addedX * wave;
      const y = line.addedY * wave;

      const screenX = line.spawnX + (line.x + x) * OPTS.len;
      const screenY = line.spawnY + (line.y + y) * OPTS.len;

      const hueOffset = getMouseHueOffset(screenX, screenY);
      const baseHue = OPTS.baseHue + ((tickRef.current * OPTS.hueChange) % 360);
      const hue = baseHue + hueOffset;
      const light =
        OPTS.baseLight +
        OPTS.addedLight *
          Math.sin(line.cumulativeTime * line.lightInputMultiplier);

      ctx.shadowBlur = prop * OPTS.shadowToTimePropMult;
      ctx.fillStyle = ctx.shadowColor = `hsl(${hue}, 100%, ${light}%)`;
      ctx.fillRect(screenX, screenY, 2, 2);

      if (Math.random() < OPTS.sparkChance) {
        ctx.fillRect(
          screenX +
            Math.random() * OPTS.sparkDist * (Math.random() < 0.5 ? 1 : -1) -
            OPTS.sparkSize / 2,
          screenY +
            Math.random() * OPTS.sparkDist * (Math.random() < 0.5 ? 1 : -1) -
            OPTS.sparkSize / 2,
          OPTS.sparkSize,
          OPTS.sparkSize,
        );
      }
    },
    [getMouseHueOffset, beginPhase],
  );

  const drawHexGrid = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const cx = w / 2;
      const cy = h / 2;

      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = `rgba(139, 92, 246, ${OPTS.gridOpacity})`;
      ctx.lineWidth = 1;

      const colsNeeded = Math.ceil(w / OPTS.hexWidth) + 2;
      const rowsNeeded = Math.ceil(h / OPTS.hexVertDist) + 2;

      for (
        let row = -Math.ceil(rowsNeeded / 2);
        row <= Math.ceil(rowsNeeded / 2);
        row++
      ) {
        for (
          let col = -Math.ceil(colsNeeded / 2);
          col <= Math.ceil(colsNeeded / 2);
          col++
        ) {
          const x =
            cx +
            col * OPTS.hexWidth +
            (Math.abs(row) % 2 === 1 ? OPTS.hexWidth / 2 : 0);
          const y = cy + row * OPTS.hexVertDist;

          ctx.beginPath();
          for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i - Math.PI / 6;
            const px = x + OPTS.hexSize * Math.cos(angle);
            const py = y + OPTS.hexSize * Math.sin(angle);
            if (i === 0) {
              ctx.moveTo(px, py);
            } else {
              ctx.lineTo(px, py);
            }
          }
          ctx.closePath();
          ctx.stroke();
        }
      }
    },
    [],
  );

  const loop = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const dims = dimsRef.current;
      const lines = linesRef.current;

      tickRef.current++;

      ctx.globalCompositeOperation = "source-over";
      ctx.shadowBlur = 0.0;

      // const alpha = Math.random() < 0.02 ? 0.12 : OPTS.repaintAlpha;
      const alpha = OPTS.repaintAlpha;
      ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
      ctx.fillRect(0, 0, dims.w, dims.h);

      ctx.globalCompositeOperation = "lighter";

      if (lines.length < OPTS.count && Math.random() < OPTS.spawnChance) {
        lines.push(createLine(true));
      }

      for (const line of lines) {
        stepLine(ctx, line);
      }

      animationFrameId.current = requestAnimationFrame(() => loop(ctx));
    },
    [createLine, stepLine],
  );

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const gridCanvas = gridCanvasRef.current;
    if (!canvas || !gridCanvas) return;

    const w = window.innerWidth;
    const h = window.innerHeight;

    canvas.width = w;
    canvas.height = h;
    gridCanvas.width = w;
    gridCanvas.height = h;

    dimsRef.current = {
      w,
      h,
      cx: w / 2,
      cy: h / 2,
      dieX: w / 2 / OPTS.len,
      dieY: h / 2 / OPTS.len,
    };

    const ctx = canvas.getContext("2d");
    const gridCtx = gridCanvas.getContext("2d");

    if (ctx) {
      ctx.fillStyle = "#0a0a12";
      ctx.fillRect(0, 0, w, h);
    }

    if (gridCtx) {
      drawHexGrid(gridCtx, w, h);
    }
  }, [drawHexGrid]);

  useEffect(() => {
    initCanvas();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY, active: true };
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        initCanvas();
      }, 150);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("resize", handleResize);

    animationFrameId.current = requestAnimationFrame(() => loop(ctx));

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [initCanvas, loop]);

  return (
    <div className={className}>
      <canvas
        ref={gridCanvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 0 }}
      />
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ zIndex: 1 }}
      />
    </div>
  );
}
